/**
 * Keuren: vijf standen, één klik per stand. Dit is het enige wat de app aan een
 * woning schrijft, en het staat op twee plekken - in het popupje op de kaart en
 * per rij in de tabel - dus het is één component met twee maten in plaats van
 * twee keer hetzelfde met een klein verschil.
 *
 * Iconen in plaats van woorden zodra het compact moet: vier woorden naast elkaar
 * maken van een tabelrij van 26 px een regel tekst waar de cijfers in verdwijnen.
 * Ze houden hun naam wel - als titel voor de muis en als `sr-only` voor wie het
 * scherm laat voorlezen.
 */
import { BEOORDELING_LABELS, BEOORDELING_VOLGORDE, type Beoordeling } from '@mamimap/shared';
import { Ban, Circle, Eye, Star, X } from 'lucide-react';
import type { ComponentType } from 'react';

type IcoonType = ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;

const ICONEN: Record<Beoordeling, IcoonType> = {
  nieuw: Circle,
  bekeken: Eye,
  kansje: Star,
  afgekeurd: X,
  definitief: Ban,
};

/**
 * Wat de muis te lezen krijgt. Bij de twee soorten nee staat het verschil erbij,
 * want aan het icoontje zie je niet dat de ene bij een terugkomst vervalt en de
 * andere niet (MA-22).
 */
const UITLEG: Partial<Record<Beoordeling, string>> = {
  afgekeurd: 'Afgekeurd - komt weer in beeld als de advertentie terugkomt',
  definitief: 'Definitief afgekeurd - blijft weg, ook als de advertentie terugkomt',
};

type Props = {
  waarde: Beoordeling;
  onKies: (beoordeling: Beoordeling) => void;
  /** Zonder woorden en op 20 px hoogte: de maat die in een tabelrij past. */
  compact?: boolean;
  /** Tijdens het opslaan; de knoppen blijven staan, ze doen alleen even niets. */
  bezig?: boolean;
  /** Waar dit over gaat, voor wie het scherm laat voorlezen. */
  omschrijving?: string;
};

export function Beoordelingsknoppen({
  waarde,
  onKies,
  compact = false,
  bezig = false,
  omschrijving,
}: Props) {
  return (
    /* Een `fieldset`, geen `div role="group"`: vier knoppen die samen één keuze
       zijn, is precies waar dit element voor is. De `legend` staat er alleen
       voor wie het scherm laat voorlezen - op het scherm zeggen de knoppen het
       zelf al. */
    <fieldset className="keurgroep" data-compact={compact ? 'true' : undefined}>
      <legend className="sr-only">
        {omschrijving ? `Oordeel over ${omschrijving}` : 'Oordeel'}
      </legend>
      {BEOORDELING_VOLGORDE.map((stand) => {
        const Icoon = ICONEN[stand];
        const actief = stand === waarde;
        const naam = BEOORDELING_LABELS[stand];
        /* Vijf woorden passen niet in het popupje; 'definitief' staat er ook
           ruim als icoon, met zijn naam voor de muis en de voorlezer. */
        const alleenIcoon = compact || stand === 'definitief';
        return (
          <button
            key={stand}
            type="button"
            className="keurknop"
            data-stand={stand}
            aria-pressed={actief}
            disabled={bezig}
            title={UITLEG[stand] ?? naam}
            /* Nog eens op dezelfde stand klikken doet niets. Zonder die regel
               schrijft elke ronde langs de lijst de keurdatum opnieuw, en dan
               zegt 'laatst gekeurd' niets meer dan 'laatst aangeklikt'. */
            onClick={() => {
              if (!actief) onKies(stand);
            }}
          >
            {/* Ruim zijn het woorden, compact iconen. Allebei tegelijk paste
                niet in het popupje van 270 px: de groep stak eruit. */}
            {alleenIcoon ? <Icoon className="h-3.5 w-3.5 shrink-0" aria-hidden={true} /> : null}
            {alleenIcoon ? <span className="sr-only">{naam}</span> : naam}
          </button>
        );
      })}
    </fieldset>
  );
}
